/**
 * L/S Book — evidence-tiered long & short candidate columns with µ scores.
 * Toggle: v2 (sector-relative µ, sector-neutral book) / v1 (vs-SPY µ).
 */
import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, RefreshCw, Scale } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { API } from "../components/stats/statsApi";

const fmtPct = (v, dp = 0) =>
  v == null ? "—" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(dp)}%`;

const TIER_COLORS = {
  L1: "bg-emerald-500/20 text-emerald-300 border-emerald-500/50",
  L2: "bg-emerald-500/15 text-emerald-400 border-emerald-500/35",
  L3: "bg-teal-500/15 text-teal-400 border-teal-500/30",
  L4: "bg-sky-500/10 text-sky-400 border-sky-500/25",
  L5: "bg-muted text-muted-foreground border-border",
  S1: "bg-red-500/20 text-red-300 border-red-500/50",
  S2: "bg-orange-500/15 text-orange-400 border-orange-500/35",
};

const TierBadge = ({ tier, tiers }) => {
  const info = tiers?.find((t) => t.tier === tier);
  return (
    <Badge
      title={info ? `${info.label}: ${info.rule}\n\nEvidence: ${info.evidence}` : tier}
      className={`text-[10px] font-mono border cursor-help ${TIER_COLORS[tier] || ""}`}
    >
      {tier}
    </Badge>
  );
};

const StateChip = ({ state }) =>
  state ? (
    <Badge className={`text-[10px] border ${
      state === "Quiet" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
        : state === "Hot" ? "bg-red-500/15 text-red-400 border-red-500/30"
        : "bg-muted text-muted-foreground border-border"}`}>
      {state}
    </Badge>
  ) : <span className="text-xs text-muted-foreground">—</span>;

const TierLegend = ({ tiers, side }) => (
  <div className="space-y-1.5">
    {tiers.map((t) => (
      <div key={t.tier} className="flex items-start gap-2 text-xs">
        <Badge className={`text-[10px] font-mono border shrink-0 ${TIER_COLORS[t.tier]}`}>
          {t.tier}
        </Badge>
        <div>
          <span className="font-semibold">{t.label}</span>
          <span className={`font-mono ml-2 ${side === "long" ? "text-emerald-400" : "text-red-400"}`}>
            µ {t.mu_note || `${t.mu > 0 ? "+" : ""}${t.mu}%/mo`}
          </span>
          <div className="text-muted-foreground">{t.rule}</div>
          <div className="text-muted-foreground/70 text-[11px]">{t.evidence}</div>
        </div>
      </div>
    ))}
  </div>
);

export default function LSBookPage() {
  const { token } = useAuth();
  const [dates, setDates] = useState([]);
  const [selDate, setSelDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMethod, setShowMethod] = useState(false);
  const [version, setVersion] = useState("v2");

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/stats/hidden-gems/screener/dates`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setDates(d.dates || []);
        if (d.dates?.length) setSelDate(d.dates[0]);
      })
      .catch(() => {});
  }, [token]);

  const load = useCallback(async (refresh = false) => {
    if (!selDate) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/stats/ls-book?date=${selDate}${refresh ? "&refresh=true" : ""}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json()).detail || res.status);
      setData(await res.json());
    } catch (e) {
      toast.error(`L/S book failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [selDate, token]);

  useEffect(() => { load(); }, [load]);

  const book = data?.books?.[version];
  const longTiers = data?.tiers?.[version]?.long || [];
  const shortTiers = data?.tiers?.[version]?.short || [];
  const methodology = data?.methodology?.[version];
  const muTitle = version === "v2"
    ? "Expected monthly SECTOR-RELATIVE alpha — what a sector-neutral book harvests (Study D)"
    : "Expected monthly alpha vs SPY — what a dollar-neutral (non-sector-neutral) book harvests";

  return (
    <div className="p-6 space-y-5" data-testid="ls-book-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Scale className="w-6 h-6 text-primary" /> L/S Book
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Evidence-tiered candidate book. Each name carries µ — its expected monthly
          alpha, set by the backtested tier it belongs to (the tier is the signal;
          equal-weight within tier). Toggle between the sector-neutral calibration (v2)
          and the vs-SPY calibration (v1).
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <select
          data-testid="lsbook-date-select"
          className="h-9 w-40 rounded-md border border-border bg-background px-2 text-sm"
          value={selDate}
          onChange={(e) => setSelDate(e.target.value)}
        >
          {dates.slice(0, 200).map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        <div className="flex rounded-md border border-border overflow-hidden" data-testid="lsbook-version-toggle">
          <button
            data-testid="lsbook-toggle-v2"
            onClick={() => setVersion("v2")}
            title="µ = sector-relative alpha. Use for a sector-neutral book: sector tilts are hedged away, only within-sector selection is harvested."
            className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
              version === "v2" ? "bg-indigo-500/25 text-indigo-300" : "text-muted-foreground hover:bg-muted"}`}
          >
            v2 · Sector-neutral
          </button>
          <button
            data-testid="lsbook-toggle-v1"
            onClick={() => setVersion("v1")}
            title="µ = alpha vs SPY. Use for a dollar-neutral book that keeps sector tilts (includes the sector-bet component of the alpha)."
            className={`px-3 py-1.5 text-xs font-semibold transition-colors border-l border-border ${
              version === "v1" ? "bg-indigo-500/25 text-indigo-300" : "text-muted-foreground hover:bg-muted"}`}
          >
            v1 · vs SPY
          </button>
        </div>

        <Button variant="outline" size="sm" disabled={loading} onClick={() => load(true)}
                data-testid="lsbook-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Recompute
        </Button>
        {data && book && (
          <span className="text-xs text-muted-foreground font-mono">
            as of {data.resolved_date} · {book.counts?.longs} longs · {book.counts?.shorts} shorts
            {data.cached ? " · cached" : ""}
          </span>
        )}
        <button
          data-testid="lsbook-methodology-toggle"
          onClick={() => setShowMethod(!showMethod)}
          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 ml-auto"
        >
          Methodology {showMethod ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {showMethod && methodology && (
        <Card data-testid="lsbook-methodology">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-xs">
            {Object.entries(methodology).map(([k, v]) => (
              <div key={k}>
                <span className="font-semibold uppercase text-[10px] text-muted-foreground">{k}</span>
                <p className="text-muted-foreground/90 mt-0.5">{v}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {loading && !data ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Computing…</div>
      ) : book && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* ---------------- LONG COLUMN ---------------- */}
          <Card className="border-emerald-500/20" data-testid="lsbook-long-column">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-emerald-400">
                Long candidates ({book.longs?.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-border text-muted-foreground">
                    <tr>
                      <th className="py-1.5 px-2 text-left">#</th>
                      <th className="py-1.5 px-2 text-left">Symbol</th>
                      <th className="py-1.5 px-2 text-left">Tier</th>
                      <th className="py-1.5 px-2 text-right" title={muTitle}>µ /mo</th>
                      <th className="py-1.5 px-2 text-center" title="Fundamentals momentum (Study 3): TTM EPS growing YoY or trailing gross margin improving vs 2q ago (point-in-time, +45d filing lag). ✓ = improving (µ bumped), ✗ = not improving (µ haircut), — = no data (µ unchanged). Longs only.">Fund</th>
                      <th className="py-1.5 px-2 text-right">Spec upside</th>
                      <th className="py-1.5 px-2 text-left">Tape</th>
                      <th className="py-1.5 px-2 text-right" title="Conviction: P(regime thesis plays out). Plain = P(delivers); ↓ = P(underperforms) for weak regimes. Informational only — it does not change µ.">Conv</th>
                      <th className="py-1.5 px-2 text-left">Regime</th>
                      <th className="py-1.5 px-2 text-right">Funds</th>
                    </tr>
                  </thead>
                  <tbody>
                    {book.longs?.map((r, i) => (
                      <tr key={r.symbol} data-testid={`lsbook-long-${r.symbol}`}
                          className="border-b border-border/40 hover:bg-muted/40">
                        <td className="py-1.5 px-2 text-muted-foreground">{i + 1}</td>
                        <td className="py-1.5 px-2 font-semibold whitespace-nowrap">
                          {r.symbol}
                          <Badge variant="outline" className="text-[9px] px-1 py-0 ml-1">
                            {r.cap_bucket === "mid_small" ? "Mid/Sm" : "Big"}
                          </Badge>
                          {r.binary_risk && (
                            <Badge className="text-[9px] px-1 py-0 ml-1 bg-amber-500/20 text-amber-400 border border-amber-500/40"
                                   title="Binary-event risk: ATM IV > 100% or expected move > 30%">
                              ⚠
                            </Badge>
                          )}
                          {r.crowded && (
                            <Badge className="text-[9px] px-1 py-0 ml-1 bg-red-500/15 text-red-400 border border-red-500/40"
                                   title="Top-decile crowding, not in a strong regime (−2.34%/3M vs SPY, t −3.49)"
                                   data-testid={`lsbook-crowded-flag-${r.symbol}`}>
                              CROWDED
                            </Badge>
                          )}
                        </td>
                        <td className="py-1.5 px-2"><TierBadge tier={r.tier} tiers={longTiers} /></td>
                        <td className="py-1.5 px-2 text-right font-mono text-emerald-400 font-semibold">
                          {r.mu > 0 ? "+" : ""}{r.mu}%
                        </td>
                        <td className="py-1.5 px-2 text-center" data-testid={`lsbook-fund-${r.symbol}`}>
                          {r.fund_momentum === true ? (
                            <span className="text-emerald-400 cursor-help" title="Fundamentals improving — µ bumped">✓</span>
                          ) : r.fund_momentum === false ? (
                            <span className="text-red-400 cursor-help" title="Fundamentals not improving — µ haircut">✗</span>
                          ) : (
                            <span className="text-muted-foreground cursor-help" title="No fundamentals data — µ unchanged">—</span>
                          )}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono">
                          {fmtPct(r.spec_upside)}
                          {r.spec_target != null && (
                            <span className="text-muted-foreground ml-1">({r.spec_target})</span>
                          )}
                        </td>
                        <td className="py-1.5 px-2"><StateChip state={r.options_state} /></td>
                        <td className="py-1.5 px-2 text-right font-mono">
                          {r.conviction != null ? (() => {
                            const weakRegime = ["Overcrowded Peak", "Distribution Phase"].includes(r.regime);
                            const v = (r.conviction * 100).toFixed(0);
                            return weakRegime ? (
                              <span className="text-red-400 cursor-help" title="Weak regime — P(underperforms)">↓{v}</span>
                            ) : (
                              <span className={`cursor-help ${r.conviction >= 0.55 ? "text-green-400" : "text-muted-foreground"}`}
                                    title="P(regime delivers)">{v}</span>
                            );
                          })() : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="py-1.5 px-2 whitespace-nowrap text-muted-foreground">
                          {r.regime || "—"}
                          {r.strong && <span className="text-emerald-400 ml-1">·S</span>}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono text-indigo-300">{r.n_extreme}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-border pt-3">
                <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-2">
                  Long tiers ({version}) — hover any tier badge for details
                </div>
                <TierLegend tiers={longTiers} side="long" />
              </div>
            </CardContent>
          </Card>

          {/* ---------------- SHORT COLUMN ---------------- */}
          <Card className="border-red-500/20" data-testid="lsbook-short-column">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-red-400 flex items-center gap-2">
                Short candidates ({book.shorts?.length})
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-muted-foreground font-normal"
                       title="Shorts are restricted to big caps for borrow availability and liquidity">
                  Big caps only
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-border text-muted-foreground">
                    <tr>
                      <th className="py-1.5 px-2 text-left">#</th>
                      <th className="py-1.5 px-2 text-left">Symbol</th>
                      <th className="py-1.5 px-2 text-left">Tier</th>
                      <th className="py-1.5 px-2 text-right" title={muTitle}>µ /mo</th>
                      <th className="py-1.5 px-2 text-left">Tape</th>
                      <th className="py-1.5 px-2 text-right" title="Historical mean fwd-3M return of the stock's regime × tape cell">µα 3M</th>
                      <th className="py-1.5 px-2 text-right" title="P(underperforms) — conviction the bearish regime plays out">↓Conv</th>
                      <th className="py-1.5 px-2 text-left">Regime</th>
                    </tr>
                  </thead>
                  <tbody>
                    {book.shorts?.map((r, i) => (
                      <tr key={r.symbol} data-testid={`lsbook-short-${r.symbol}`}
                          className="border-b border-border/40 hover:bg-muted/40">
                        <td className="py-1.5 px-2 text-muted-foreground">{i + 1}</td>
                        <td className="py-1.5 px-2 font-semibold">{r.symbol}</td>
                        <td className="py-1.5 px-2"><TierBadge tier={r.tier} tiers={shortTiers} /></td>
                        <td className="py-1.5 px-2 text-right font-mono text-red-400 font-semibold">
                          {r.mu}%
                        </td>
                        <td className="py-1.5 px-2"><StateChip state={r.options_state} /></td>
                        <td className={`py-1.5 px-2 text-right font-mono ${
                          r.micro_alpha_3m != null && r.micro_alpha_3m < 3 ? "text-red-400" : "text-muted-foreground"}`}>
                          {r.micro_alpha_3m != null ? `${r.micro_alpha_3m > 0 ? "+" : ""}${r.micro_alpha_3m}%` : "—"}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono">
                          {r.p_under != null ? (
                            <span className={r.p_under >= 0.55 ? "text-red-400 font-semibold" : "text-muted-foreground"}>
                              ↓{(r.p_under * 100).toFixed(0)}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="py-1.5 px-2 whitespace-nowrap text-muted-foreground">{r.regime}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-border pt-3">
                <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-2">
                  Short tiers ({version}) — hover any tier badge for details
                </div>
                <TierLegend tiers={shortTiers} side="short" />
                <p className="text-[11px] text-muted-foreground mt-2">
                  Exclusivity: any name in the long candidate list is barred from the short book.
                  {version === "v2"
                    ? " v2 shorts = weak+Hot only (the v1 'weak non-Hot µα' tier failed sector-relative — its alpha was sector tilt)."
                    : " v1 includes the 'weak non-Hot µα' tier (valid vs SPY; fails sector-relative)."}
                  {" "}The short leg is a hedge — it earns relative alpha, not absolute return in a rising market.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
